import styled from 'styled-components';
import { getColumnWidth } from '@nile/components';

const getAccessor = (col) => {
  switch (col.name) {
    case 'prioritizedId':
      return 'displayId';
    case 'displayId':
      return 'displayId';
    case 'followers':
      return undefined;
    case 'resolutionTimer':
      return 'resolutionDueTimeLeft';
    case 'firstResponseTimer':
      return 'firstResponseDueTimeLeft';
    case 'taskSummary':
      return 'totalTasks';
    default:
      return col.name;
  }
};

const getType = (col) => {
  if (col.isCustom) {
    switch (col.uiDataType) {
      case 'paragraph':
      case 'text':
      case 'numeric':
      case 'decimal':
        return 'CustomText';
      case 'date':
      case 'datetime':
        return 'CustomTime';
      case 'radio':
        return 'CustomRadio';
      case 'checkbox':
        return 'CustomCheckbox';
      case 'select':
      case 'multi_select':
        return 'CustomSelect';
      case 'secure_text':
        return 'SecureText';
      default:
        return 'Text';
    }
  }
  switch (col.name) {
    case 'prioritizedId':
      return 'Priority';
    case 'requester':
    case 'technician':
    case 'techGroup':
    case 'client':
      return 'Dual';
    case 'resolutionTimer':
    case 'firstResponseTimer':
      return 'Timer';
    case 'status':
      return 'Status';
    case 'subject':
      return 'HTML';
    case 'firstResponseDueTime':
    case 'resolutionDueTime':
    case 'firstResponseTime':
    case 'createdTime':
    case 'resolutionTime':
    case 'updatedTime':
      return 'DateTime';
    case 'cause':
    case 'subcause':
    case 'category':
    case 'subcategory':
    case 'issue':
    case 'subissue':
      return 'Dependant';
    case 'worklogTimespent':
      return 'HoursMinutes';
    case 'taskSummary':
      return 'Progress';
    default:
      return 'Text';
  }
};

const getCellType = (col) => {
  switch (col.name) {
    case 'prioritizedId':
      return 'Priority';
    case 'technician':
    case 'techGroup':
    case 'client':
    case 'requester':
      return 'Dual';
    case 'resolutionTimer':
      return 'Timer';
    case 'subject':
      return 'HTML';
    case 'firstResponseDueTime':
    case 'resolutionDueTime':
    case 'firstResponseTime':
    case 'createdTime':
    case 'resolutionTime':
    case 'updatedTime':
      return 'DateTime';
    case 'cause':
    case 'subcause':
    case 'category':
    case 'subcategory':
    case 'issue':
    case 'subissue':
      return 'Dependant';
    case 'followers':
      return 'MultiSelectText';
    case 'additionalRequester':
      return 'AsyncMultiSelectUser';
    // case 'taskSummary':
    //   return 'Progress'
    default:
      return 'Text';
  }
};

const isEditable = (col) => {
  if (col.isCustom) {
    return true;
  }
  switch (col.name) {
    case 'subject':
    case 'impact':
    case 'status':
    case 'priority':
    case 'urgency':
    case 'cause':
    case 'subcause':
    case 'category':
    case 'subcategory':
    case 'technician':
    case 'techGroup':
    case 'sop':
    case 'followers':
    case 'additionalRequester':
    case 'client':
    case 'site':
    case 'requester':
    case 'resolutionCode':
    case 'ticketType':
      return true;
    default:
      return false;
  }
};

const isClearable = (col) => {
  if (col.isCustom) {
    return false;
  }
  switch (col.name) {
    case 'impact':
    case 'urgency':
    case 'priority':
    case 'cause':
    case 'category':
    case 'subcategory':
    case 'subcause':
    case 'resolutionCode':
    case 'technician':
    case 'techGroup':
    case 'followers':
    case 'sop':
    case 'additionalRequester':
      return isEditable(col) && true;
    default:
      return false;
  }
};

const isSortable = (col, availableAttributes) => {
  if (availableAttributes.indexOf(col.name) > -1) return true;
  switch (col.name) {
    default:
      return false;
  }
};

const isFilterable = (allowedProperties, col) => {
  if (col.uiDataType === 'secure_text') {
    return false;
  }
  switch (col?.name) {
    case 'prioritizedId':
      return true;
    default:
      return allowedProperties.some((el) => el.name === col.name);
  }
};

const getTicketFields = (personalizedColumns, isMSPProduct) => {
  let fields = personalizedColumns.filter((el) => !el.isCustom);
  let fieldsToRequest = [
    {
      listInfo: [
        'page',
        'pageSize',
        'hasMore',
        'totalCount',
        'condition',
        {
          sort: ['attribute', 'order']
        }
      ]
    }
  ];
  let allFields = [];
  fields.forEach((el) => {
    switch (el.name) {
      case 'techGroup':
        allFields.push({
          techGroup: ['groupId', 'name']
        });
        break;
      case 'client':
        allFields.push({
          client: ['name', 'accountId']
        });
        break;
      case 'requester':
        allFields.push({
          requester: ['firstName', 'lastName', 'userId', 'name']
        });
        break;
      case 'department':
        allFields.push({
          department: ['businessFunctionId', 'name']
        });
        break;
      case 'sla':
        allFields.push({
          sla: ['name', 'id']
        });
        break;
      case 'sop':
        allFields.push({
          sop: ['name']
        });
        break;
      case 'approvals':
        break;
      case 'followers':
        allFields.push({
          followers: ['firstName', 'lastName', 'userId', 'name']
        });
        break;
      case 'additionalRequester':
        allFields.push({
          additionalRequester: ['firstName', 'lastName', 'userId', 'name']
        });
        break;
      case 'createdBy':
        allFields.push({
          createdBy: ['firstName', 'lastName', 'userId']
        });
        break;
      case 'resolutionTimer':
        allFields = allFields.concat([
          'resolutionDueTimeLeft',
          'resolutionDueTime',
          'resolutionTime',
          'resolutionViolated',
          'resolutionPauseTime'
        ]);
        break;
      case 'firstResponseTimer':
        allFields = allFields.concat([
          'firstResponseDueTimeLeft',
          'firstResponseDueTime',
          'firstResponseTime',
          'firstResponseViolated'
        ]);
        break;
      case 'site':
        allFields.push({
          site: ['id', 'name']
        });
        break;
      case 'department':
        allFields.push({
          department: ['businessFunctionId', 'name']
        });
        break;
      case 'displayId':
        allFields.push('displayId');
        break;
      case 'taskSummary':
        allFields = allFields.concat(['totalTasks', 'completedTasks']);
        break;
      case 'prioritizedId':
        allFields = allFields.concat([
          'priority',
          'ticketType',
          'displayId',
          'source'
        ]);
        break;
      default:
        if (el.name !== 'technician') {
          allFields.push(el.name);
        }
    }
  });
  const allAddtionalFields = [
    'approverPermission',
    'followerPermission',
    'ticketId',
    'source',
    { latestReply: ['type', { user: ['userId', 'email'] }] },
    'readStatus',
    { site: ['name', 'id'] },
    'numberOfUnreadVendorConversation',
    'createdTime',
    {
      technician: ['userId', 'email', 'firstName', 'lastName', 'name']
    }
  ];
  if (isMSPProduct) {
    allAddtionalFields.push({
      client: ['name', 'accountId']
    });
  }
  allFields = allFields.concat(allAddtionalFields);
  if (personalizedColumns.length !== fields.length) {
    const customFieldsArray = personalizedColumns
      .filter((el) => el.isCustom)
      .map((el) => el.name);

    allFields.push(`customFields(
      fields: ${JSON.stringify(customFieldsArray)}
    )
    `);
  }

  fieldsToRequest.push({
    tickets: allFields
  });
  return fieldsToRequest;
};
// Depened field is mapping dependentfiledname : dependencyfieldname
const dependencyMap = {
  subcause: 'cause',
  subcategory: 'category',
  technician: 'techGroup',
  requester: 'client'
};

const isDependantField = (field) => {
  return Object.keys(dependencyMap).indexOf(field) > -1;
};

const getParentField = (child) => {
  return dependencyMap[child];
};

const getDependantField = (parent) => {
  return Object.keys(dependencyMap).find(
    (key) => dependencyMap[key] === parent
  );
};

const getRuleAttribute = (f) => {
  switch (f.name) {
    case 'prioritizedId':
      return 'displayId';
    default:
      return f.name;
  }
};

const getWidth = (name) => {
  switch (name) {
    case 'prioritizedId':
      return {
        minWidth: 200,
        width: 220
      };
    case 'subject':
      return {
        minWidth: 100,
        width: 400
      };
    default:
      return {
        minWidth: 50,
        width: 180
      };
  }
};

const getWidthFromPersonalization = (name, baseObj) => {
  switch (name) {
    case 'prioritizedId':
      return getColumnWidth(baseObj, name, 200);
    case 'subject':
      return getColumnWidth(baseObj, name, 200);
    default:
      return getColumnWidth(baseObj, name, 180);
  }
};

const DisabledTag = styled.div`
  opacity: 0.6;
`;
const CellWrapper = styled.div`
  width: 100%;
  .Polaris-Link {
    width: 100%;
  }
`;

const getQueryVariables = (params) => {
  const listInfo = params?.listInfo;
  const variables = {
    listInfo: {
      value: listInfo,
      type: 'ListInfoInput',
      required: true
    }
  };
  return variables;
};

export {
  getAccessor,
  getType,
  getCellType,
  isEditable,
  isClearable,
  isSortable,
  isFilterable,
  getTicketFields,
  DisabledTag,
  dependencyMap,
  getDependantField,
  getParentField,
  getRuleAttribute,
  isDependantField,
  CellWrapper,
  getWidth as getColumnWidth,
  getWidthFromPersonalization,
  getQueryVariables
};
